import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosHeaders,
} from "axios";
import Logger from "../utils/Logger";
import ErrorResponse from "./ErrorResponse";
import {
  LocalStorage,
  RegionLocalStorage,
  UserDeviceStorage,
} from "../utils/Utils";
import { LoginResponse } from "../features/login/data/response/LoginResponse";
import { LoginRequest } from "../features/login/data/request/LoginRequest";
import LoginClient from "../features/login/data/api/LoginClient";
import Loader from "../utils/Utils";

const baseURL = process.env.REACT_APP_API_END_POINT;
// const baseURL = "http://127.0.0.1:8000";

interface AlertConfig {
  identity: string;
  show: boolean;
  title: string;
  message: string;
  button1Text: string;
  button2Text?: string;
  alertType: string;
  btn2close: boolean;
  onlyNeedOneButton: boolean;
  btn1onPressed: () => void;
  btn2onPressed?: () => void;
  bottomHintText?: string;
  multipleLoginProp?: string;
}

interface QueuedRequest {
  resolve: (value: any) => void;
  reject: (error: any) => void;
  request: any;
}

class HttpService {
  private email: string | null;
  private password: string | null;
  private api: AxiosInstance;
  private showAlert: (alertConfig: AlertConfig) => void;
  private isRefreshing: boolean = false;
  private requestQueue: QueuedRequest[] = [];
  private retryConfigs: Map<string, InternalAxiosRequestConfig> = new Map(); // Track retry configs

  constructor(showAlert: (alertConfig: any) => void) {
    this.api = axios.create({
      baseURL,
      timeout: 30000,
      timeoutErrorMessage:
        "Unable to reach the server. Please check your internet connection",
    });
    this.showAlert = showAlert;

    // Initialize email and password from URL
    this.email = this.getEmailFromURL();
    this.password = this.getPasswordFromURL();

    if (this.email != null) {
      LocalStorage.setLocalData("email_address", this.email);
    }
    if (this.password != null) {
      LocalStorage.setLocalData("entity_password", this.password);
    }

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request Interceptor - FIXED VERSION
    this.api.interceptors.request.use(
      async (
        config: InternalAxiosRequestConfig,
      ): Promise<InternalAxiosRequestConfig> => {
        Logger.info("Entered Request interceptor", config);

        // Check if this is a retry request by looking for our retry marker
        const isRetryRequest = this.retryConfigs.has(
          this.getRequestKey(config),
        );

        // If we're offline AND this is not a retry request
        if (!window.navigator.onLine && !isRetryRequest) {
          Loader.dismissLoading();

          // Store this config as a potential retry
          this.retryConfigs.set(this.getRequestKey(config), config);

          try {
            // Show alert and wait for user action
            // If user clicks retry and it succeeds, this will return successfully
            await this.showNetworkErrorAlert(config);

            // If we reach here, the retry was successful
            // Remove from retry configs and RETURN THE ORIGINAL CONFIG to continue
            this.retryConfigs.delete(this.getRequestKey(config));

            // Apply headers and continue with the original request
            this.applyHeaders(config);
            Logger.info(
              "Offline retry successful, proceeding with request",
              config,
            );
            return config;
          } catch (error) {
            // If retry failed or user didn't retry, remove from configs and reject
            this.retryConfigs.delete(this.getRequestKey(config));
            Logger.error("Offline retry failed or cancelled", error);
            return Promise.reject(new Error("No internet connection")) as any;
          }
        }

        // If this is a retry request, remove it from the tracking
        if (isRetryRequest) {
          this.retryConfigs.delete(this.getRequestKey(config));
          Logger.info(
            "Processing retry request, bypassing offline check",
            config,
          );
        }

        // Apply headers to the config
        this.applyHeaders(config);

        Logger.info("Request with headers", config);
        return config;
      },
      (error: any): Promise<never> => {
        Logger.info("Request interceptor error", error);
        return Promise.reject(error);
      },
    );

    // Response Interceptor
    this.api.interceptors.response.use(
      (response: AxiosResponse): AxiosResponse => {
        Logger.info("Response success", response);
        return response;
      },
      async (error: AxiosError<ErrorResponse>): Promise<any> => {
        Logger.info("Response interceptor error", error);

        const originalRequest = error.config;

        // If no original request, just reject
        if (!originalRequest) {
          return Promise.reject(error);
        }

        const emailAddress: string | null =
          LocalStorage.getLocalData("email_address");
        const entitypassword: string | null =
          LocalStorage.getLocalData("entity_password");

        // // Handle timeout errors
        // if (
        //   error.code === "ECONNABORTED" ||
        //   error.message?.includes("timeout")
        // ) {
        //   Loader.dismissLoading();
        //   try {
        //     const response = await this.handleTimeoutWithRetry(originalRequest);
        //     return response;
        //   } catch (retryError) {
        //     return Promise.reject(retryError);
        //   }
        // }
        // Handle timeout errors - DISTINGUISH BETWEEN CONNECTION AND RESPONSE TIMEOUT
        if (
          error.code === "ECONNABORTED" ||
          error.message?.includes("timeout")
        ) {
          Loader.dismissLoading();

          // Check if we have a partial response (headers arrived but body didn't complete)
          const hasPartialResponse =
            error.response && error.response.status === 200;

          if (hasPartialResponse) {
            Logger.info(
              "Partial response received - response body timed out",
              error.response,
            );
            await this.handleTimeoutWithRetry(originalRequest);
            return Promise.reject(
              new Error("Response body timed out - slow network"),
            );
          } else {
            // No response at all - connection timeout
            Logger.info("Connection timeout - unable to reach server", error);
            if (originalRequest) {
              try {
                const response =
                  await this.handleTimeoutWithRetry(originalRequest);
                Logger.info("Connection timeout - Response", response);

                return response;
              } catch (retryError) {
                return Promise.reject(retryError);
              }
            }
          }
        }

        // Handle 504 Gateway Timeout
        if (error.response?.status === 504) {
          Loader.dismissLoading();
          try {
            const response = await this.handleTimeoutWithRetry(originalRequest);
            return response;
          } catch (retryError) {
            return Promise.reject(retryError);
          }
        }

        // Handle 401 Unauthorized
        if (
          error.response?.status === 401 &&
          error.response.data?.detail === undefined
        ) {
          if (emailAddress && entitypassword) {
            const originalRequestWithRetry = originalRequest as any;
            if (!originalRequestWithRetry._retry) {
              originalRequestWithRetry._retry = true;
              try {
                const response = await this.handle401ErrorWithRetry(
                  originalRequestWithRetry,
                );
                return response;
              } catch (retryError) {
                return Promise.reject(retryError);
              }
            }
          } else {
            this.handleSessionExpired();
          }
        }

        // Handle session expired
        if (
          error.response?.status === 401 &&
          error!.response!.data?.detail === null
        ) {
          this.handleSessionExpired();
        }

        if (error.response) {
          // Handle other specific status codes
          switch (error.response.status) {
            case 401:
              // Handle 401 while accessing directly to the path without login
              if (error.response?.data.detail) {
                window.history.replaceState({}, "", "/");
                window.location.reload();
              }
              break;
            case 440:
              this.handle440Error();
              break;
            case 500:
              this.handle500Error(error);
              break;
            case 422:
              this.handle422Error();
              break;
            default:
              if (error.response.data?.message) {
                this.handleGenericError(error.response.data.message);
              } else {
                this.handleUnknownError();
              }
              break;
          }
        }

        return Promise.reject(error);
      },
    );
  }

  // Generate a unique key for each request to track retries
  private getRequestKey(config: InternalAxiosRequestConfig): string {
    return `${config.method}-${config.url}-${JSON.stringify(
      config.data,
    )}-${JSON.stringify(config.params)}`;
  }

  // Apply headers to the request config
  private applyHeaders(
    config: InternalAxiosRequestConfig | AxiosRequestConfig,
  ): void {
    // Ensure headers object exists
    if (!config.headers) {
      config.headers = new AxiosHeaders();
    }

    // Get region and set API key
    const region = LocalStorage.getLocalData("region");
    let apiKey: string | undefined;

    // Set the API key based on the region
    switch (region) {
      case "ap-south-1":
        apiKey = process.env.REACT_APP_API_SERVICE_KEY_SOUTH;
        break;
      case "us-east-1":
        apiKey = process.env.REACT_APP_API_SERVICE_KEY_US_EAST;
        break;
      case "ap-southeast-4":
        apiKey = process.env.REACT_APP_API_SERVICE_KEY_SOUTH_EAST;
        break;
      // default:
      //   apiKey = process.env.REACT_APP_API_SERVICE_KEY_SOUTH;
      //   break;
    }

    if (
      (!apiKey || apiKey.trim() === "") &&
      config.url &&
      !config.url.includes("region")
    ) {
      window.history.replaceState({}, "", "/");
      window.location.reload(); // Optional: Reload to reflect changes
    }

    // Set API key header
    if (apiKey) {
      (config.headers as AxiosHeaders).set("x-api-key", apiKey);
    }

    // No device-id  & Token configuration for the Region api
    if (config.url && !config.url.includes("region")) {
      const Email = LocalStorage.getLocalData("email_address");
      const deviceId = UserDeviceStorage.getDeviceId(Email!);
      if (deviceId) {
        (config.headers as AxiosHeaders).set("Device-Id", deviceId);
      }
      // Ensure Authorization header is preserved if it exists
      const token = LocalStorage.getLocalData("access_token");
      if (token) {
        (config.headers as AxiosHeaders).set(
          "Authorization",
          `Bearer ${token}`,
        );
      }
    }

    // Ensure Content-Type is set for requests with data
    if (config.data && !(config.headers as AxiosHeaders).get("Content-Type")) {
      (config.headers as AxiosHeaders).set("Content-Type", "application/json");
    }
  }

  // Show network error alert (for offline scenario) - FIXED
  private async showNetworkErrorAlert(
    originalConfig: InternalAxiosRequestConfig,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      this.showAlert({
        identity: "default",
        show: true,
        title: "Error",
        message:
          "Unable to reach the server. Please check your internet connection.",
        button1Text: "Retry",
        alertType: "error",
        btn2close: false,
        onlyNeedOneButton: true,
        btn1onPressed: async () => {
          try {
            Loader.showLoading();
            Logger.info("User clicked retry, checking network status...");

            // // Double-check network status before proceeding
            // if (!window.navigator.onLine) {
            //   Logger.warn("Still offline, cannot retry");
            //   this.showAlert({
            //     identity: "default",
            //     show: true,
            //     title: "Error",
            //     message:
            //       "Unable to reach the server. Please check your internet connection.",
            //     button1Text: "OK",
            //     alertType: "error",
            //     btn2close: false,
            //     onlyNeedOneButton: true,
            //     btn1onPressed: () => {
            //       reject(new Error("Still offline"));
            //     },
            //   });
            //   return;
            // }

            Logger.info("Network is online, proceeding with retry");

            // Ensure headers are applied before retry
            this.applyHeaders(originalConfig);
            Logger.info("Retrying request with headers", originalConfig);

            // Mark this as a retry request
            this.retryConfigs.set(
              this.getRequestKey(originalConfig),
              originalConfig,
            );

            // Retry the original request
            const response = await this.api.request(originalConfig);
            resolve(response as any);
          } catch (error) {
            Logger.error("Offline retry failed", error);
            reject(error);
          } finally {
            Loader.dismissLoading();
          }
        },
      });
    });
  }

  // Handle timeout with retry that returns the response to the original caller
  private async handleTimeoutWithRetry(
    originalRequest: InternalAxiosRequestConfig,
  ): Promise<AxiosResponse> {
    return new Promise((resolve, reject) => {
      this.showAlert({
        identity: "default",
        show: true,
        title: "Error",
        message:
          "Unable to reach the server. Please check your internet connection.",
        button1Text: "Retry",
        button2Text: "Diagnose network",
        alertType: "error",
        btn2close: false,
        onlyNeedOneButton: false,
        btn1onPressed: async () => {
          try {
            Loader.showLoading();

            // Mark this as a retry request
            this.retryConfigs.set(
              this.getRequestKey(originalRequest),
              originalRequest,
            );

            // Ensure headers are applied before retry
            this.applyHeaders(originalRequest);
            Logger.info(
              "Retrying timeout request with headers",
              originalRequest,
            );

            // Retry the original request
            const response = await this.api.request(originalRequest);
            Logger.info(
              "Retry successful, returning response to component",
              response,
            );
            resolve(response);
          } catch (error) {
            Logger.error("Retry failed", error);
            reject(error);
          } finally {
            Loader.dismissLoading();
          }
        },
        btn2onPressed: () => {
          this.showSpeedTest();
          reject(new Error("User chose speed test"));
        },
      });
    });
  }

  // Handle 401 error with token refresh and retry - SIMPLIFIED
  private async handle401ErrorWithRetry(
    originalRequest: any,
  ): Promise<AxiosResponse> {
    try {
      // Refresh the token
      await this.login();

      const token = LocalStorage.getLocalData("access_token");

      // Apply headers including the new token
      this.applyHeaders(originalRequest);

      if (token) {
        originalRequest.headers["Authorization"] = `Bearer ${token}`;
      }

      // Mark this as a retry request
      this.retryConfigs.set(
        this.getRequestKey(originalRequest),
        originalRequest,
      );

      // Retry the original request
      const response = await this.api.request(originalRequest);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ... rest of your methods remain the same (getTokenFromURL, getEmailFromURL, etc.)
  // Extract values from URL functions
  private getTokenFromURL(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("token");
  }

  private getEmailFromURL(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("email_address");
  }

  private getPasswordFromURL(): string | null {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("entity_password");
  }

  // Set the Authorization token
  setAuthToken(token: string | null): void {
    if (token) {
      this.api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete this.api.defaults.headers.common["Authorization"];
    }
  }

  // Method to login and refresh the token
  async login(): Promise<AxiosResponse<LoginResponse>> {
    try {
      const emailAddress: string | null =
        LocalStorage.getLocalData("email_address");
      const entitypassword: string | null =
        LocalStorage.getLocalData("entity_password");

      const param: LoginRequest = {
        email_address: emailAddress || "",
        entity_password: entitypassword || "",
        entity_type: "USER",
      };

      // Clear the authorization token before making the request
      this.setAuthToken(null);

      // Create config for login request
      const config: AxiosRequestConfig = {
        method: "POST",
        url: LoginClient.token,
        data: param,
      };

      // Apply headers manually for login request
      this.applyHeaders(config);

      const response: AxiosResponse<LoginResponse> =
        await this.api.request(config);

      const deviceId_header: string | undefined = response.headers["device-id"];
      if (deviceId_header && emailAddress) {
        UserDeviceStorage.setDeviceId(emailAddress, deviceId_header);
      }

      if (response.data.access_token) {
        const newToken: string = response.data.access_token;
        LocalStorage.setLocalData("access_token", newToken);
        this.setAuthToken(newToken);
        Logger.info("Token refreshed successfully", newToken);
      }

      return response;
    } catch (error) {
      Logger.error("Failed to refresh token", error);
      throw error;
    }
  }

  // HTTP methods
  async get<T>(
    endpoint: string,
    params?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.api.get<T>(endpoint, { ...config, params });
  }

  async post<T>(
    endpoint: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.api.post<T>(endpoint, data, config);
  }

  async put<T>(
    endpoint: string,
    data: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.api.put<T>(endpoint, data, config);
  }

  async delete<T>(
    endpoint: string,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.api.delete<T>(endpoint, config);
  }

  async patch<T>(
    endpoint: string,
    data: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.api.patch<T>(endpoint, data, config);
  }

  // Error handler methods
  private handleSessionExpired(): void {
    this.showAlert({
      identity: "default",
      show: true,
      title: "Session Expired",
      message: "Session timed out. Please login again",
      button1Text: "OK",
      alertType: "error",
      btn2close: false,
      onlyNeedOneButton: true,
      btn1onPressed: (): void => {
        LocalStorage.deleteAllLocalData();
        window.history.replaceState({}, "", "/");
        window.location.reload();
      },
    });
  }

  // private showSpeedTest(): void {
  //   const overlay = document.createElement("div");
  //   overlay.style.position = "fixed";
  //   overlay.style.top = "0";
  //   overlay.style.left = "0";
  //   overlay.style.width = "100%";
  //   overlay.style.height = "100%";
  //   overlay.style.background = "rgba(0,0,0,0.6)";
  //   overlay.style.zIndex = "100000000000";
  //   overlay.style.display = "flex";
  //   overlay.style.alignItems = "center";
  //   overlay.style.justifyContent = "center";

  //   const iframe = document.createElement("iframe");
  //   iframe.src = "/speedtest";
  //   iframe.width = "50%";
  //   iframe.height = "60%";
  //   iframe.style.border = "none";
  //   iframe.style.borderRadius = "8px";
  //   iframe.style.background = "#fff";

  //   const closeOverlay = (): void => {
  //     if (document.body.contains(overlay)) {
  //       document.body.removeChild(overlay);
  //     }
  //     window.removeEventListener("message", handleMessage);
  //     overlay.removeEventListener("click", handleOverlayClick);
  //   };

  //   const handleOverlayClick = (e: MouseEvent): void => {
  //     if (e.target === overlay) closeOverlay();
  //   };

  //   const handleMessage = (event: MessageEvent): void => {
  //     if (event.data?.type === "CLOSE_SPEED_TEST") closeOverlay();
  //   };

  //   overlay.addEventListener("click", handleOverlayClick as EventListener);
  //   window.addEventListener("message", handleMessage);
  //   overlay.appendChild(iframe);
  //   document.body.appendChild(overlay);
  // }
  private showSpeedTest(): void {
    // Create overlay background
    const overlay = document.createElement("div");
    overlay.style.cssText = `
    position: fixed;
    top: 0; left: 0;
    width: 100%; height: 100%;
    background: rgba(0,0,0,0.6);
    z-index: 1000000;
    display: flex;
    align-items: center;
    justify-content: center;
  `;

    // Loader container with background
    const loaderContainer = document.createElement("div");
    loaderContainer.style.cssText = `
    padding: 20px;
    border-radius: 12px;
    background-color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
     width: 50%; height: 60%;
  `;

    // Loader spinner
    const loader = document.createElement("div");
    loader.style.cssText = `
    border: 6px solid #f3f3f3;
    border-top: 6px solid #18232e;
    border-radius: 50%;
    width: 60px; height: 60px;
    animation: spin 1s linear infinite;
    background-color: #fff;
  `;
    loaderContainer.appendChild(loader);
    overlay.appendChild(loaderContainer);

    // Add keyframes for spinner animation
    const style = document.createElement("style");
    style.innerHTML = `
    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  `;
    document.head.appendChild(style);

    // Iframe for SpeedTest
    const iframe = document.createElement("iframe");
    iframe.src = "/speedtest"; // your path
    iframe.width = "50%";
    iframe.height = "60%";
    iframe.style.cssText = `
    border: none;
    border-radius: 8px;
    background: #fff;
    display: none; /* hide until ready */
  `;

    // Handle messages from iframe
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === "SPEEDTEST_READY") {
        // Hide loader and show iframe
        loaderContainer.style.display = "none";
        iframe.style.display = "block";
      } else if (event.data?.type === "CLOSE_SPEED_TEST") {
        closeOverlay();
      }
    };
    window.addEventListener("message", handleMessage);

    // Close overlay function
    const closeOverlay = () => {
      if (document.body.contains(overlay)) document.body.removeChild(overlay);
      window.removeEventListener("message", handleMessage);
    };

    // Close overlay if clicked outside iframe
    const handleOverlayClick = (e: MouseEvent) => {
      if (e.target === overlay) closeOverlay();
    };
    overlay.addEventListener("click", handleOverlayClick as EventListener);

    // Append iframe to overlay
    overlay.appendChild(iframe);

    // Add overlay to body
    document.body.appendChild(overlay);
  }

  private handle440Error(): void {
    this.showAlert({
      identity: "multiuser-warning",
      show: true,
      title: "Warning!",
      message:
        "Your session on this device was ended because <b>you logged in from a new device or browser.<b>",
      bottomHintText:
        "To protect your account, <b>only one active session is allowed </b> at a time.",
      button1Text: "Login Again",
      button2Text: "",
      alertType: "error",
      multipleLoginProp: "Session_Ended",
      btn2close: false,
      onlyNeedOneButton: false,
      btn1onPressed: (): void => {
        LocalStorage.deleteAllLocalData();
        window.history.replaceState({}, "", "/");
        window.location.reload();
      },
    });
  }

  private handle500Error(error: AxiosError<ErrorResponse>): void {
    this.showAlert({
      identity: "default",
      show: true,
      title: "Error",
      message: error.response?.data?.error || "Internal server error",
      button1Text: "OK",
      alertType: "error",
      btn2close: false,
      onlyNeedOneButton: true,
      btn1onPressed: (): void => { },
    });
  }

  private handle422Error(): void {
    this.showAlert({
      identity: "default",
      show: true,
      title: "Error",
      message: "Missing keys in the payload",
      button1Text: "OK",
      alertType: "error",
      btn2close: false,
      onlyNeedOneButton: true,
      btn1onPressed: (): void => { },
    });
  }

  private handleGenericError(message: string): void {
    this.showAlert({
      identity: "default",
      show: true,
      title: "Error",
      message: message,
      button1Text: "OK",
      alertType: "error",
      btn2close: false,
      onlyNeedOneButton: true,
      btn1onPressed: (): void => { },
    });
  }

  private handleUnknownError(): void {
    this.showAlert({
      identity: "default",
      show: true,
      title: "Error",
      message: "Oops! Something went wrong. Please try again later.",
      button1Text: "Cancel",
      alertType: "error",
      btn2close: false,
      onlyNeedOneButton: true,
      btn1onPressed: (): void => { },
    });
  }
}

export default HttpService;
